import {
	Check,
	Clock,
	Copy,
	Eye,
	EyeOff,
	RefreshCw,
	Star,
	Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Toast from "@/components/_shared/Toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { usePageSettings } from "@/lib/cookies";

// Standard-Einstellungen für den Passwort-Generator
const defaultSettings = {
	length: 32,
	upper: true,
	lower: true,
	number: true,
	special: true,
	noSimilar: true,
	noAmbiguous: true,
	minUpper: 1,
	minNumber: 1,
	minSpecial: 1,
};

interface PasswordOptions {
	length: number;
	upper: boolean;
	lower: boolean;
	number: boolean;
	special: boolean;
	noSimilar: boolean;
	noAmbiguous: boolean;
	minUpper: number;
	minNumber: number;
	minSpecial: number;
}

// Interface für Verlaufseinträge
interface HistoryEntry {
	password: string;
	timestamp: number;
}

// Interface für angeheftete Passwörter
interface PinnedEntry {
	name: string;
	password: string;
}

function generatePassword(options: PasswordOptions) {
	const {
		length,
		upper,
		lower,
		number,
		special,
		noSimilar,
		noAmbiguous,
		minUpper,
		minNumber,
		minSpecial,
	} = options;

	// Zeichensätze definieren
	let upperChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
	let lowerChars = "abcdefghijklmnopqrstuvwxyz";
	let numberChars = "0123456789";
	let specialChars = "!@#$%^&*()-_=+[]{};:,.<>?";

	// Ähnliche Zeichen entfernen
	if (noSimilar) {
		upperChars = upperChars.replace(/[IO]/g, "");
		lowerChars = lowerChars.replace(/[l]/g, "");
		numberChars = numberChars.replace(/[01]/g, "");
	}

	// Mehrdeutige Zeichen entfernen
	if (noAmbiguous) {
		specialChars = specialChars.replace(/[{}()[\]<>:.;,]/g, "");
	}

	// Zeichensätze nach Optionen erstellen
	let chars = "";
	if (upper) chars += upperChars;
	if (lower) chars += lowerChars;
	if (number) chars += numberChars;
	if (special) chars += specialChars;

	if (!chars) return "";

	// Mindestanforderungen sicherstellen
	let pwd = "";
	const requirements = [];

	if (upper && minUpper > 0) {
		for (let i = 0; i < minUpper; i++) {
			requirements.push(
				upperChars.charAt(Math.floor(Math.random() * upperChars.length)),
			);
		}
	}

	if (number && minNumber > 0) {
		for (let i = 0; i < minNumber; i++) {
			requirements.push(
				numberChars.charAt(Math.floor(Math.random() * numberChars.length)),
			);
		}
	}

	if (special && minSpecial > 0) {
		for (let i = 0; i < minSpecial; i++) {
			requirements.push(
				specialChars.charAt(Math.floor(Math.random() * specialChars.length)),
			);
		}
	}

	// Restliche Zeichen auffüllen
	for (let i = requirements.length; i < length; i++) {
		pwd += chars.charAt(Math.floor(Math.random() * chars.length));
	}

	// Requirements einfügen und mischen
	requirements.forEach((char) => {
		const position = Math.floor(Math.random() * pwd.length);
		pwd = pwd.slice(0, position) + char + pwd.slice(position + 1);
	});

	return pwd;
}

// Helper-Funktion zum Färben verschiedener Zeichen im Passwort
function ColorizedPassword({ password }: { password: string }) {
	if (!password) return null;

	return (
		<div className="flex select-all flex-wrap font-mono text-base md:text-lg">
			{Array.from(password).map((char, i) => {
				let className = "transition-colors";
				if (/[A-Z]/.test(char))
					className += " text-blue-600 dark:text-blue-400";
				else if (/[a-z]/.test(char))
					className += " text-gray-800 dark:text-gray-300";
				else if (/[0-9]/.test(char))
					className += " text-emerald-600 dark:text-emerald-400";
				else className += " text-rose-600 dark:text-rose-400";

				return (
					<span className={className} key={i}>
						{char}
					</span>
				);
			})}
		</div>
	);
}

export default function PasswortForm() {
	const { settings, updateSettings } = usePageSettings(
		"passwort",
		defaultSettings,
	);
	const [password, setPassword] = useState("");
	const [history, setHistory] = useState<HistoryEntry[]>([]);
	const [pined, setPined] = useState<PinnedEntry[]>(() => {
		try {
			const raw = localStorage.getItem("passwort_pinned");
			return raw ? JSON.parse(raw) : [];
		} catch {
			return [];
		}
	});
	const [pinPrompt, setPinPrompt] = useState(false);
	const [pinName, setPinName] = useState("");
	const pinPwdRef = useRef<string>("");
	const [copied, setCopied] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [showPinedPasswordIdx, setShowPinedPasswordIdx] = useState<
		number | null
	>(null);

	// Passwort beim ersten Laden generieren
	useEffect(() => {
		handleGenerate();
		// Verlaufseinträge werden nur in der aktuellen Session gespeichert und nicht mehr aus sessionStorage geladen
	}, []);

	// Neues Passwort generieren und zum Verlauf hinzufügen
	const handleGenerate = () => {
		const pwd = generatePassword(settings);
		// Altes Passwort in den Verlauf verschieben
		if (password) {
			setHistory((h) => [
				{ password, timestamp: Date.now() },
				...h.slice(0, 9),
			]);
		}
		// Neues Passwort setzen
		setPassword(pwd);
		setSuccessMessage("Neues Passwort generiert");
	};

	// Passwort in die Zwischenablage kopieren
	const handleCopy = (pwd = password) => {
		if (!pwd) return;
		navigator.clipboard.writeText(pwd).then(() => {
			setCopied(pwd);
			setSuccessMessage("Passwort kopiert");
			setTimeout(() => setCopied(null), 2000);
		});
	};

	// Passwort anheften
	const handlePin = () => {
		setPinPrompt(true);
		pinPwdRef.current = password;
		setPinName("");
	};

	// Anheften bestätigen
	const confirmPin = () => {
		if (!pinName.trim()) return;
		const newPined = [
			...pined,
			{ name: pinName.trim(), password: pinPwdRef.current },
		];
		setPined(newPined);
		localStorage.setItem("passwort_pinned", JSON.stringify(newPined));
		setPinPrompt(false);
		setSuccessMessage(`"${pinName.trim()}" wurde angeheftet`);
	};

	// Angeheftetes Passwort entfernen
	const unpin = (idx: number) => {
		const name = pined[idx].name;
		const newPined = pined.filter((_, i) => i !== idx);
		setPined(newPined);
		localStorage.setItem("passwort_pinned", JSON.stringify(newPined));
		setSuccessMessage(`"${name}" wurde entfernt`);
	};

	// Einstellung ändern und speichern
	const handleOptionChange = (key: string, value: any) => {
		updateSettings({ [key]: value });

		// Wenn die Länge geändert wird, direkt neues Passwort generieren
		if (key === "length") {
			const newSettings = { ...settings, [key]: value };
			const pwd = generatePassword(newSettings);
			setPassword(pwd);
		}
	};

	// Verlaufseintrag löschen
	const removeFromHistory = (idx: number) => {
		setHistory((h) => {
			const newHistory = h.filter((_, i) => i !== idx);
			return newHistory;
		});
		setSuccessMessage("Eintrag aus Verlauf entfernt");
	};

	// Toast-Feedback
	useEffect(() => {
		if (successMessage) {
			const timer = setTimeout(() => {
				setSuccessMessage(null);
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	// Passwort-Stärke berechnen (0-100)
	const calculateStrength = (pwd: string) => {
		if (!pwd) return 0;
		let strength = 0;

		// Länge: bis zu 50 Punkte
		strength += Math.min(pwd.length * 2, 50);

		// Zeichenvielfalt: bis zu 50 Punkte
		const hasUpper = /[A-Z]/.test(pwd);
		const hasLower = /[a-z]/.test(pwd);
		const hasNumber = /[0-9]/.test(pwd);
		const hasSpecial = /[^A-Za-z0-9]/.test(pwd);

		strength += hasUpper ? 12.5 : 0;
		strength += hasLower ? 12.5 : 0;
		strength += hasNumber ? 12.5 : 0;
		strength += hasSpecial ? 12.5 : 0;

		return Math.min(100, strength);
	};

	// Stärke-Farbe
	const getStrengthColor = (strength: number) => {
		if (strength >= 80) return "bg-emerald-500";
		if (strength >= 60) return "bg-green-500";
		if (strength >= 40) return "bg-yellow-500";
		if (strength >= 20) return "bg-orange-500";
		return "bg-rose-500";
	};

	return (
		<div className="flex flex-col gap-8">
			{/* Success message toast */}
			<Toast
				message={successMessage || ""}
				onClose={() => setSuccessMessage(null)}
				visible={!!successMessage}
			/>

			{/* OBERER BEREICH: Passwort-Generator und angeheftete Passwörter */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h2 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-800 text-xl dark:border-gray-700 dark:text-gray-100">
					Passwort generieren
				</h2>

				{/* Generator-Optionen in Form-Layout wie bei TOTP */}
				<div className="mb-6 flex flex-col">
					<div className="mb-4 flex flex-col items-end gap-4 md:flex-row">
						<div className="flex-1">
							<Label
								className="mb-2 block font-medium text-gray-600 dark:text-gray-300"
								htmlFor="password-length"
							>
								Länge: {settings.length} Zeichen
							</Label>
							<Slider
								className="mb-1"
								id="password-length"
								max={64}
								min={8}
								onValueChange={(v) => handleOptionChange("length", v[0])}
								step={1}
								value={[settings.length]}
							/>
						</div>
						<div className="flex w-full gap-2 md:w-auto">
							<Button
								className="w-full transition-all hover:bg-blue-100 md:w-auto dark:hover:bg-blue-900/30"
								onClick={handleGenerate}
								type="button"
								variant="secondary"
							>
								<RefreshCw className="mr-2 h-4 w-4" />
								In Verlauf
							</Button>
							<Button
								className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 md:w-auto dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
								disabled={!password}
								onClick={handlePin}
								type="button"
								variant="outline"
							>
								<Star className="mr-2 h-4 w-4" />
								Anheften
							</Button>
						</div>
					</div>

					{/* Checkboxen für die Passwort-Optionen */}
					<div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 md:grid-cols-3 dark:border-gray-700 dark:bg-gray-900/30">
						<label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
							<input
								checked={settings.upper}
								className="rounded"
								onChange={(e) => handleOptionChange("upper", e.target.checked)}
								type="checkbox"
							/>
							<span>Großbuchstaben</span>
						</label>
						<label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
							<input
								checked={settings.lower}
								className="rounded"
								onChange={(e) => handleOptionChange("lower", e.target.checked)}
								type="checkbox"
							/>
							<span>Kleinbuchstaben</span>
						</label>
						<label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
							<input
								checked={settings.number}
								className="rounded"
								onChange={(e) => handleOptionChange("number", e.target.checked)}
								type="checkbox"
							/>
							<span>Zahlen</span>
						</label>
						<label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
							<input
								checked={settings.special}
								className="rounded"
								onChange={(e) =>
									handleOptionChange("special", e.target.checked)
								}
								type="checkbox"
							/>
							<span>Sonderzeichen</span>
						</label>
						<label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
							<input
								checked={settings.noSimilar}
								className="rounded"
								onChange={(e) =>
									handleOptionChange("noSimilar", e.target.checked)
								}
								type="checkbox"
							/>
							<span>Keine ähnlichen Zeichen</span>
						</label>
						<label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
							<input
								checked={settings.noAmbiguous}
								className="rounded"
								onChange={(e) =>
									handleOptionChange("noAmbiguous", e.target.checked)
								}
								type="checkbox"
							/>
							<span>Keine mehrdeutigen Zeichen</span>
						</label>
					</div>
				</div>

				{/* Aktuelles Passwort Anzeige - ähnlicher Stil wie TOTP-Code */}
				<div className="mb-6 flex flex-col items-center gap-6 rounded-lg border border-gray-100 bg-gray-50 p-4 md:flex-row dark:border-gray-700 dark:bg-gray-800">
					<button
						className={`flex max-w-full items-center gap-2 overflow-x-auto rounded-lg px-2 py-1 font-mono font-semibold text-4xl tracking-widest transition-all ${
							password
								? "group cursor-pointer text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
								: "cursor-not-allowed text-gray-400 dark:text-gray-500"
						}`}
						disabled={!password}
						onClick={() => handleCopy(password)}
						type="button"
					>
						<ColorizedPassword password={password} />
						{copied === password ? (
							<Check className="h-5 w-5 shrink-0 text-emerald-500 opacity-100 transition-opacity dark:text-emerald-400" />
						) : (
							<Copy className="h-5 w-5 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
						)}
					</button>

					<div className="flex items-center gap-3 md:ml-auto">
						<div className="relative flex h-10 w-10 shrink-0 items-center justify-center">
							{/* Background circle */}
							<svg
								className="-rotate-90 h-10 w-10 transform"
								viewBox="0 0 40 40"
							>
								<circle
									className="stroke-gray-200 dark:stroke-gray-700"
									cx="20"
									cy="20"
									fill="none"
									r="18"
									strokeWidth="3"
								/>

								{/* Strength circle */}
								<circle
									className={`transition-all duration-300 ${getStrengthColor(calculateStrength(password)).replace("bg-", "stroke-")}`}
									cx="20"
									cy="20"
									fill="none"
									r="18"
									strokeDasharray={2 * Math.PI * 18}
									strokeDashoffset={
										2 * Math.PI * 18 * (1 - calculateStrength(password) / 100)
									}
									strokeLinecap="round"
									strokeWidth="3"
								/>
							</svg>

							{/* Length text */}
							<span className="absolute inset-0 flex items-center justify-center font-medium text-xs tabular-nums">
								{settings.length}
							</span>
						</div>
						<div>
							<span className="font-medium text-gray-600 text-sm dark:text-gray-300">
								{calculateStrength(password) >= 80
									? "Sehr stark"
									: calculateStrength(password) >= 60
										? "Stark"
										: calculateStrength(password) >= 40
											? "Mittel"
											: calculateStrength(password) >= 20
												? "Schwach"
												: "Sehr schwach"}
							</span>
						</div>
					</div>
				</div>

				{/* Angeheftete Passwörter */}
				{pined.length > 0 && (
					<div className="mt-6">
						<h3 className="mb-3 font-semibold text-gray-700 text-sm uppercase tracking-wider dark:text-gray-200">
							Angeheftete Passwörter
						</h3>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
							{pined.map((entry, idx) => (
								<div
									className="flex flex-col rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
									key={idx}
								>
									<div className="mb-2 truncate font-bold text-blue-600 text-lg dark:text-blue-400">
										{entry.name}
									</div>
									<div className="mb-3 flex items-center justify-between">
										<button
											className="group flex max-w-full cursor-pointer items-center gap-2 overflow-x-auto rounded-lg font-mono font-semibold text-gray-900 text-lg tracking-wide transition-all hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
											onClick={() => handleCopy(entry.password)}
											type="button"
										>
											<ColorizedPassword password={entry.password} />
											{copied === entry.password ? (
												<Check className="h-4 w-4 shrink-0 text-emerald-500 opacity-100 transition-opacity dark:text-emerald-400" />
											) : (
												<Copy className="h-4 w-4 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
											)}
										</button>

										<div className="relative flex h-10 w-10 shrink-0 items-center justify-center">
											{/* Background circle */}
											<svg
												className="-rotate-90 h-10 w-10 transform"
												viewBox="0 0 40 40"
											>
												<circle
													className="stroke-gray-200 dark:stroke-gray-700"
													cx="20"
													cy="20"
													fill="none"
													r="18"
													strokeWidth="3"
												/>

												{/* Strength circle */}
												<circle
													className={`transition-all duration-300 ${
														calculateStrength(entry.password) >= 80
															? "stroke-emerald-500"
															: calculateStrength(entry.password) >= 60
																? "stroke-green-500"
																: calculateStrength(entry.password) >= 40
																	? "stroke-yellow-500"
																	: calculateStrength(entry.password) >= 20
																		? "stroke-orange-500"
																		: "stroke-rose-500"
													}`}
													cx="20"
													cy="20"
													fill="none"
													r="18"
													strokeDasharray={2 * Math.PI * 18}
													strokeDashoffset={
														2 *
														Math.PI *
														18 *
														(1 - calculateStrength(entry.password) / 100)
													}
													strokeLinecap="round"
													strokeWidth="3"
												/>
											</svg>

											{/* Length text */}
											<span className="absolute inset-0 flex items-center justify-center font-medium text-xs tabular-nums">
												{settings.length}
											</span>
										</div>
									</div>

									<div className="mt-auto flex justify-between border-gray-100 border-t pt-2 dark:border-gray-700">
										<div />
										<Button
											className="text-gray-600 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400"
											onClick={() => unpin(idx)}
											size="sm"
											type="button"
											variant="ghost"
										>
											<Trash2 className="h-4 w-4" />
											<span className="ml-1">Entfernen</span>
										</Button>
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</section>

			{/* UNTERER BEREICH: Verlauf */}
			<section className="card rounded-xl border border-gray-100 p-6 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-800/70">
				<h3 className="mb-4 border-gray-100 border-b pb-2 font-semibold text-gray-700 text-lg dark:border-gray-700 dark:text-gray-200">
					Verlauf{" "}
					<span className="font-normal text-gray-500 text-sm dark:text-gray-400">
						(letzte 10, nur diese Session)
					</span>
				</h3>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{history.length === 0 && (
						<div className="col-span-full rounded-lg border border-gray-100 bg-gray-50 p-4 text-center text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500">
							Noch keine Passwörter im Verlauf. Klicke auf "Neues Passwort", um
							Einträge hier anzuzeigen.
						</div>
					)}

					{history.map((entry, idx) => (
						<div
							className="flex flex-col rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
							key={idx}
						>
							<div className="mb-2 flex items-center text-gray-500 text-sm dark:text-gray-400">
								<Clock className="mr-1 h-4 w-4" />
								{new Date(entry.timestamp).toLocaleTimeString()}
							</div>
							<div className="mb-3 flex items-center justify-between">
								<button
									className="group flex max-w-full cursor-pointer items-center gap-2 overflow-x-auto rounded-lg font-mono font-semibold text-gray-900 text-lg tracking-wide transition-all hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
									onClick={() => handleCopy(entry.password)}
									type="button"
								>
									<ColorizedPassword password={entry.password} />
									{copied === entry.password ? (
										<Check className="h-4 w-4 shrink-0 text-emerald-500 opacity-100 transition-opacity dark:text-emerald-400" />
									) : (
										<Copy className="h-4 w-4 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500" />
									)}
								</button>

								<div className="relative flex h-10 w-10 shrink-0 items-center justify-center">
									{/* Background circle */}
									<svg
										className="-rotate-90 h-10 w-10 transform"
										viewBox="0 0 40 40"
									>
										<circle
											className="stroke-gray-200 dark:stroke-gray-700"
											cx="20"
											cy="20"
											fill="none"
											r="18"
											strokeWidth="3"
										/>

										{/* Strength circle */}
										<circle
											className={`transition-all duration-300 ${
												calculateStrength(entry.password) >= 80
													? "stroke-emerald-500"
													: calculateStrength(entry.password) >= 60
														? "stroke-green-500"
														: calculateStrength(entry.password) >= 40
															? "stroke-yellow-500"
															: calculateStrength(entry.password) >= 20
																? "stroke-orange-500"
																: "stroke-rose-500"
											}`}
											cx="20"
											cy="20"
											fill="none"
											r="18"
											strokeDasharray={2 * Math.PI * 18}
											strokeDashoffset={
												2 *
												Math.PI *
												18 *
												(1 - calculateStrength(entry.password) / 100)
											}
											strokeLinecap="round"
											strokeWidth="3"
										/>
									</svg>

									{/* Length text */}
									<span className="absolute inset-0 flex items-center justify-center font-medium text-xs tabular-nums">
										{settings.length}
									</span>
								</div>
							</div>

							<div className="mt-auto flex justify-between gap-2 border-gray-100 border-t pt-2 dark:border-gray-700">
								<Button
									className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
									onClick={() => {
										pinPwdRef.current = entry.password;
										setPinPrompt(true);
										setPinName("");
									}}
									size="sm"
									type="button"
									variant="ghost"
								>
									<Star className="mr-1 h-4 w-4" />
									Anheften
								</Button>
								<Button
									className="text-gray-600 hover:text-rose-600 dark:text-gray-400 dark:hover:text-rose-400"
									onClick={() => removeFromHistory(idx)}
									size="sm"
									type="button"
									variant="ghost"
								>
									<Trash2 className="mr-1 h-4 w-4" />
									Entfernen
								</Button>
							</div>
						</div>
					))}
				</div>
			</section>

			{/* Pin-Dialog */}
			{pinPrompt && (
				<div className="fade-in fixed inset-0 z-50 flex animate-in items-center justify-center bg-black/60 backdrop-blur-sm">
					<div className="zoom-in-95 w-full max-w-sm animate-in rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-800">
						<h4 className="mb-3 font-semibold text-gray-800 text-lg dark:text-gray-100">
							Passwort anheften
						</h4>
						<p className="mb-4 text-gray-500 text-sm dark:text-gray-400">
							Gib einen Namen für das angeheftete Passwort ein. Angeheftete
							Passwörter bleiben auch nach dem Neuladen der Seite erhalten.
						</p>
						<Label
							className="mb-2 block text-gray-600 dark:text-gray-300"
							htmlFor="pin-name"
						>
							Name
						</Label>
						<Input
							autoFocus
							className="mb-4 border-gray-200 bg-white focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:focus:ring-blue-600"
							id="pin-name"
							onChange={(e) => setPinName(e.target.value)}
							placeholder="z.B. Gmail-Passwort"
							value={pinName}
						/>
						<div className="flex justify-end gap-3">
							<Button
								className="text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
								onClick={() => setPinPrompt(false)}
								type="button"
								variant="ghost"
							>
								Abbrechen
							</Button>
							<Button
								className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
								disabled={!pinName.trim()}
								onClick={confirmPin}
								type="button"
							>
								<Star className="mr-2 h-4 w-4" />
								Anheften
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
